import React, { useRef, useState, useEffect } from "react";
import { ErrorMessage, Description, Field, Label } from "../common/fieldset";
import { Input } from "../common/input";
import { Textarea } from "../common/textarea";
import { Select } from "../common/select";
import { Switch, SwitchField } from "../common/switch";
import SliderComponent from "../common/Slider";
import InputFieldList from "../common/InputFieldList";
import { Radio, RadioField, RadioGroup } from "../common/radio";
import { Listbox, ListboxLabel, ListboxOption } from "../common/listbox";
import { ListboxWithSections } from "./ListBoxGrouped";
import { Checkbox } from "../common/checkbox";
import { DocumentArrowUpIcon } from "@heroicons/react/24/outline";

const InputComponent = ({
  name,
  label,
  value,
  error,
  onChange,
  type,
  style,
  placeholder,
  options,
  customClass,
  description,
  info,
  min,
  max,
  defaultValue,
  step,
  invalid,
  disabled,
  descriptionWithInfo,
  addLabel,
  addPlaceholder,
  sliderMarks,
  onFileChange,
}) => {
  const csvFileInputRef = useRef(null);
  const [fileMeta, setFileMeta] = useState(null);

  useEffect(() => {
    if (!value) setFileMeta(null);
  }, [value]);

  return (
    <div className="h-full">
      {(type === "text" ||
        type === "number" ||
        type === "password" ||
        type === "color" ||
        type === "email") && (
        <>
          <Field>
            <Label>{label}</Label>
            <Description>{description}</Description>
            {descriptionWithInfo && descriptionWithInfo}
            <Input
              placeholder={placeholder}
              type={type}
              name={name}
              value={value}
              onChange={onChange}
              invalid={invalid}
              disabled={disabled}
              min={min}
              max={max}
            />
            {(error != {} || undefined) && <ErrorMessage>{error}</ErrorMessage>}
            {info && info}
          </Field>
        </>
      )}
      {type === "textArea" && (
        <Field className="flex-1 h-full w-full">
          <Label>{label}</Label>
          <Description>{description}</Description>
          <Textarea
            style={style}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
          />
          {(error != {} || undefined) && <ErrorMessage>{error}</ErrorMessage>}
        </Field>
      )}
      {/* {type === "select" && (
        <Field>
          <Label>{label}</Label>
          <Description>{description}</Description>
          <Select
            name={name}
            onChange={onChange}
            value={value}
            defaultValue=""
          >
            {options.map((option) => (
              <option
                key={option.id}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </Select>
          {(error != {} || undefined) && <ErrorMessage>{error}</ErrorMessage>}
          {info && info}
        </Field>
      )} */}
      {type === "select" && (
        <Field>
          <Label>{label}</Label>
          <Description>{description}</Description>
          <Listbox
            as="div"
            className="my-4"
            value={value}
            disabled={disabled}
            defaultValue={value}
            onChange={(value) => {
              onChange({ target: { value }, currentTarget: { name, value } });
            }}
          >
            {options.map((option) => (
              <ListboxOption
                value={option.value}
                key={option.id}
                disabled={option.disabled}
              >
                {option.icon && option.icon}
                <ListboxLabel className="flex justify-between w-full">
                  {option.label}
                  {option.badge && option.badge}
                </ListboxLabel>
              </ListboxOption>
            ))}
          </Listbox>
          {(error != {} || undefined) && <ErrorMessage>{error}</ErrorMessage>}
          {info && info}
        </Field>
      )}
      {type === "grouped-select" && (
        <Field>
          <Label>{label}</Label>
          <Description>{description}</Description>
          <ListboxWithSections
            as="div"
            className="my-4"
            options={options}
            value={value}
            defaultValue={value}
            onChange={(value) => {
              onChange({ target: { value }, currentTarget: { name, value } });
            }}
          />
          {info && info}
        </Field>
      )}

      {type === "toggle" && (
        <div className="space-y-2">
          <SwitchField>
            <Label>{label}</Label>
            <Description>{description}</Description>
            {descriptionWithInfo && descriptionWithInfo}
            <Switch
              checked={value}
              name={name}
              disabled={disabled}
              onChange={(isActive) => {
                onChange({
                  currentTarget: {
                    name: name,
                    value: isActive,
                  },
                  toggleValue: isActive,
                });
              }}
            />
          </SwitchField>
          {info && info}
        </div>
      )}

      {type === "slider" && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-medium text-gray-700">{label}</label>
            <span className="text-sm font-semibold bg-slate-200 rounded-lg p-1 text-black px-2 py-1">
              {value}
            </span>
          </div>
          <p className="text-xs text-gray-500 mb-4">{description}</p>
          <SliderComponent
            min={min}
            max={max}
            marks={sliderMarks}
            onChange={onChange}
            value={value}
            name={name}
            step={step}
            disabled={disabled}
          />
        </div>
      )}

      {type === "checkbox-custom-label" && (
        <div>
          <div className="flex items-start gap-x-2">
            <Checkbox
              color="blue"
              className="mt-1"
              checked={value}
              onChange={(isActive) => {
                onChange({
                  currentTarget: {
                    name: name,
                    value: isActive,
                  },
                });
              }}
            />
            {label && label}
          </div>
        </div>
      )}

      {type === "inputFieldList" && (
        <Field>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                {label}
              </label>
            </div>
            <p className="text-xs text-gray-500 mb-4">{description}</p>
            <InputFieldList
              name={name}
              value={value}
              onChange={onChange}
              addLabel={addLabel}
              addPlaceholder={addPlaceholder}
              disabled={disabled}
            />
          </div>
          {(error != {} || undefined) && <ErrorMessage>{error}</ErrorMessage>}
        </Field>
      )}

      {type === "radio" && (
        <Field>
          <Label>{label}</Label>
          <Description>{description}</Description>
          <RadioGroup
            name={name}
            defaultValue={value}
          >
            {options.map((option) => (
              <RadioField key={option.id}>
                <Radio
                  value={option.value}
                  onClick={(e) =>
                    onChange({
                      currentTarget: {
                        name: name,
                        value: option.value,
                      },
                    })
                  }
                />
                <Label>{option.label}</Label>
              </RadioField>
            ))}
          </RadioGroup>
          {(error != {} || undefined) && <ErrorMessage>{error}</ErrorMessage>}
        </Field>
      )}

      {type === "csv" && (
        <Field>
          <Label>{label}</Label>
          <Description>{description}</Description>
          <button
            type="button"
            onClick={() => csvFileInputRef.current?.click()}
            className="w-full rounded-lg border-2 border-dashed border-zinc-300 dark:border-zinc-700 p-8 text-center hover:border-zinc-400
  dark:hover:border-zinc-600 transition-colors cursor-pointer mb-2"
          >
            <DocumentArrowUpIcon className="mx-auto size-10 text-zinc-400" />
            <p className="mt-2 text-sm font-medium text-zinc-700 dark:text-zinc-300">
              {fileMeta?.fileName ||
                placeholder ||
                "Click to select a CSV file"}
            </p>
            {fileMeta &&
              (fileMeta.total > 0 ? (
                <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                  {fileMeta.total} contacts found
                  {fileMeta.skipped > 0 &&
                    ` · ${fileMeta.skipped} rows skipped (missing phone)`}
                </p>
              ) : (
                <p className="mt-1 text-xs text-red-600 dark:text-red-500">
                  No valid contacts found. CSV must have a non-empty "phone"
                  column.
                </p>
              ))}
          </button>
          <input
            ref={csvFileInputRef}
            type="file"
            accept=".csv"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (onFileChange) {
                onFileChange(file, onChange, name, setFileMeta);
              } else {
                onChange({ currentTarget: { name, value: file } });
              }
            }}
            className="hidden"
          />
          {(error != {} || undefined) && <ErrorMessage>{error}</ErrorMessage>}
          {info && info}
        </Field>
      )}
    </div>
  );
};

export default InputComponent;
